import { useNavigate } from "react-router";

import { LoginAppRoutes, SjAdminAppRoutes, KaAppRoutes } from "../constants/routes";
import { checkUserLicenseStatus } from "../api";
import {
  retrieveTenantList,
  saveSelectedTenant,
  removeCurrentSelectedTenant,
  saveUsingCognitoCookies,
  saveAuthToken,
  cleanUserArtifacts,
  setIsSjAdminToStorage,
} from "../utils";
import { savePathForRedirect, getAndRemovePathForRedirect } from "../utils/redirectStorage";

export const useLoginFlow = () => {
  let navigate;
  try {
    navigate = useNavigate();
  } catch (_e) {
    // Handle the case where useNavigate is not available
    navigate = (path: string) => {
      window.location.assign(path);
    };
  }

  const handleRedirectAfterLogin = async () => {
    const redirectPath = getAndRemovePathForRedirect() || "/";
    window.location.assign(redirectPath);
  };

  const handlePostLogin = async (token: string, sjAdmin: boolean, isDemo: boolean = false) => {
    saveAuthToken(token);
    saveUsingCognitoCookies();

    // If this is an sjAdmin login, save that to localStorage and then redirect to sj-admin dashboard
    if (sjAdmin) {
      setIsSjAdminToStorage();
      window.location.assign(SjAdminAppRoutes.route);

      return;
    }

    // If isDemo is true, skip tenant selection and license check, and go straight to demo
    if (isDemo) {
      window.location.href = KaAppRoutes.routes.demoLandingPage.route;
      return;
    }

    // Handle tenant selection flow
    const tenant = await initialTenantSelection();
    if (tenant) {
      // Only one tenant in auth token: select it
      const success = await doTenantSelectionAndRedirect(tenant);

      if (!success) {
        // License check failed, redirect to license error page
        navigate(LoginAppRoutes.routes.unlicensed.route);
      }
    } else {
      // Multiple tenants in auth token: go to tenant selection page
      navigate(LoginAppRoutes.routes.tenantSelection.route);
    }
  };

  // Helper function to handle tenant selection & redirect
  const doTenantSelectionAndRedirect = async (tenant: string): Promise<boolean> => {
    // 1. Check user license for the selected tenant
    const hasLicense = await checkUserLicense(tenant);
    if (!hasLicense) {
      return false; // License check failed, so don't store tenant or redirect
    }

    // 2. Save selected tenant
    saveSelectedTenant(tenant);

    // 3. Redirect to appropriate post-login page
    handleRedirectAfterLogin();
    return true;
  };

  const handleUnauthedAccess = () => {
    savePathForRedirect(window.location.pathname + window.location.search);
    cleanUserArtifacts();
    navigate(LoginAppRoutes.routes.signedOut.route);
  };

  return {
    handlePostLogin,
    doTenantSelectionAndRedirect,
    handleRedirectAfterLogin,
    handleUnauthedAccess,
  };
};

// Helper function to handle initial tenant selection
const initialTenantSelection = async (): Promise<string | null> => {
  const tenantList = await retrieveTenantList();

  if (tenantList.length === 1) {
    const selectedTenant = tenantList[0];
    return selectedTenant;
  } else {
    removeCurrentSelectedTenant();
    return null;
  }
};

// Helper function to check user license status in a specific tenant
const checkUserLicense = async (tenant_name: string): Promise<boolean> => {
  return await checkUserLicenseStatus(tenant_name);
};
